import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { alertsApi, devicesApi, geofencesApi, beaconApi, usersApi, fuelApi, type FuelCard } from '../services/api';
import { useHasModule } from '../store/modules';
import { useTenantContextStore } from '../store/tenant-context.store';
import { useAuthStore } from '../store/auth.store';
import {
  Bell,
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight,
  X,
  Save,
  Clock,
  Mail,
  Smartphone,
  MessageCircle,
  Gauge,
  MapPin,
  Zap,
  Battery,
  AlertTriangle,
  Shield,
  Volume2,
  Truck,
  ChevronDown,
  ChevronUp,
  Info,
  List,
  Settings,
  Play,
  Square,
  TrendingUp,
  TrendingDown,
  CornerDownRight,
  Fuel,
  Send,
  CheckCircle,
  XCircle,
  HelpCircle,
  Loader2,
  Radio,
  Users,
  Power,
  CheckSquare,
} from 'lucide-react';
import clsx from 'clsx';

type AlertType =
  | 'GEOFENCE_ENTER'
  | 'GEOFENCE_EXIT'
  | 'OVERSPEED'
  | 'UNDERSPEED'
  | 'IGNITION_ON'
  | 'IGNITION_OFF'
  | 'DEVICE_OFFLINE'
  | 'BATTERY_LOW'
  | 'FUEL_LOW'
  | 'IDLE_PROLONGED'
  | 'TOWING'
  | 'CRASH'
  | 'JAMMING'
  | 'SOS'
  | 'POWER_CUT'
  | 'POWER_RESTORED'
  | 'LOW_POWER'
  | 'DEVICE_MOVING'
  | 'DEVICE_STOPPED'
  | 'HARD_ACCELERATION'
  | 'HARD_BRAKING'
  | 'HARD_CORNERING'
  | 'FUEL_DROP'
  | 'FUEL_INCREASE'
  | 'FUEL_THRESHOLD'
  | 'BEACON_NEAR'
  | 'BEACON_AWAY'
  | 'BEACON_LOST';

type AlertSeverity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

interface AlertRule {
  id: string;
  name: string;
  description?: string;
  type: AlertType;
  isActive: boolean;
  conditions: Record<string, unknown>;
  severity: AlertSeverity;
  notifyEmail: boolean;
  notifyPush: boolean;
  notifySms: boolean;
  notifyWhatsapp: boolean;
  emailRecipients: string[];
  cooldownMinutes: number;
  scheduleEnabled: boolean;
  scheduleDays: number[];
  scheduleStart?: string;
  scheduleEnd?: string;
  deviceIds: string[];
  recipientUserIds?: string[];
  geofences: Array<{ id: string; name: string }>;
  createdAt: string;
  updatedAt: string;
}

interface Device {
  id: string;
  name: string;
  plate?: string;
  imei: string;
}

interface Geofence {
  id: string;
  name: string;
  type: string;
}

/**
 * Static alert-type config: icon and condition metadata.
 * Labels/descriptions live in i18n (`alert_rules.type_<KEY>_label/desc`).
 */
const alertTypeConfig: Record<AlertType, { icon: React.ElementType; hasConditions: boolean; conditionFields?: string[] }> = {
  OVERSPEED: { icon: Gauge, hasConditions: true, conditionFields: ['speedLimit'] },
  UNDERSPEED: { icon: Gauge, hasConditions: true, conditionFields: ['minSpeed'] },
  GEOFENCE_ENTER: { icon: MapPin, hasConditions: false },
  GEOFENCE_EXIT: { icon: MapPin, hasConditions: false },
  IGNITION_ON: { icon: Zap, hasConditions: false },
  IGNITION_OFF: { icon: Zap, hasConditions: false },
  DEVICE_OFFLINE: { icon: AlertTriangle, hasConditions: true, conditionFields: ['offlineMinutes'] },
  BATTERY_LOW: { icon: Battery, hasConditions: true, conditionFields: ['threshold'] },
  FUEL_LOW: { icon: Gauge, hasConditions: true, conditionFields: ['threshold'] },
  IDLE_PROLONGED: { icon: Clock, hasConditions: true, conditionFields: ['durationMinutes'] },
  TOWING: { icon: Truck, hasConditions: false },
  CRASH: { icon: AlertTriangle, hasConditions: false },
  JAMMING: { icon: Shield, hasConditions: false },
  SOS: { icon: Volume2, hasConditions: false },
  POWER_CUT: { icon: Zap, hasConditions: true, conditionFields: ['threshold'] },
  POWER_RESTORED: { icon: Zap, hasConditions: true, conditionFields: ['threshold'] },
  LOW_POWER: { icon: Battery, hasConditions: true, conditionFields: ['threshold'] },
  DEVICE_MOVING: { icon: Play, hasConditions: true, conditionFields: ['speedThreshold'] },
  DEVICE_STOPPED: { icon: Square, hasConditions: true, conditionFields: ['speedThreshold', 'minStopDuration'] },
  HARD_ACCELERATION: { icon: TrendingUp, hasConditions: true, conditionFields: ['threshold'] },
  HARD_BRAKING: { icon: TrendingDown, hasConditions: true, conditionFields: ['threshold'] },
  HARD_CORNERING: { icon: CornerDownRight, hasConditions: true, conditionFields: ['threshold', 'minSpeed'] },
  FUEL_DROP: { icon: Fuel, hasConditions: true, conditionFields: ['threshold', 'timeWindow'] },
  FUEL_INCREASE: { icon: Fuel, hasConditions: true, conditionFields: ['threshold', 'timeWindow'] },
  // Custom condition set : on rend une UI dédiée (metric/period/scope/cards/threshold)
  FUEL_THRESHOLD: { icon: Fuel, hasConditions: true, conditionFields: ['threshold', 'metric', 'period', 'scope', 'fuelCardIds'] },
  BEACON_NEAR: { icon: Radio, hasConditions: true, conditionFields: ['beaconId'] },
  BEACON_AWAY: { icon: Radio, hasConditions: true, conditionFields: ['beaconId'] },
  BEACON_LOST: { icon: Radio, hasConditions: true, conditionFields: ['beaconId'] },
};

const severityOptions: { value: AlertSeverity; color: string }[] = [
  { value: 'LOW', color: 'bg-blue-100 text-blue-800' },
  { value: 'MEDIUM', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'HIGH', color: 'bg-orange-100 text-orange-800' },
  { value: 'CRITICAL', color: 'bg-red-100 text-red-800' },
];

const DAY_KEYS = ['day_sun', 'day_mon', 'day_tue', 'day_wed', 'day_thu', 'day_fri', 'day_sat'] as const;

const defaultRule: Partial<AlertRule> = {
  name: '',
  description: '',
  type: 'OVERSPEED',
  isActive: true,
  conditions: { speedLimit: 120 },
  severity: 'MEDIUM',
  notifyEmail: true,
  notifyPush: true,
  notifySms: false,
  notifyWhatsapp: false,
  emailRecipients: [],
  cooldownMinutes: 5,
  scheduleEnabled: false,
  scheduleDays: [1, 2, 3, 4, 5],
  scheduleStart: '08:00',
  scheduleEnd: '18:00',
  deviceIds: [],
  recipientUserIds: [],
};

export default function AlertRulesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { selectedTenant } = useTenantContextStore();
  const tenantKey = selectedTenant?.id || 'default';
  const { user } = useAuthStore();
  const perms = user?.tenantPermissions || [];
  const canEdit = user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN'
    || perms.includes('alerts:edit') || perms.includes('alerts:full');
  const canDelete = user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN'
    || perms.includes('alerts:full');

  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<AlertType | ''>('');
  const [activeFilter, setActiveFilter] = useState<boolean | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AlertRule | null>(null);
  const [formData, setFormData] = useState<Partial<AlertRule>>(defaultRule);
  const [expandedSections, setExpandedSections] = useState({
    conditions: true,
    notifications: true,
    recipients: false,
    schedule: false,
    devices: false,
  });
  const [testingNotification, setTestingNotification] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ type: string; success: boolean; message: string; preview?: any } | null>(null);

  // Multi-selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkRecipientsModal, setShowBulkRecipientsModal] = useState(false);
  const [bulkRecipientUserIds, setBulkRecipientUserIds] = useState<string[]>([]);

  // Clear selection when filters change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [typeFilter, activeFilter, searchQuery]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredRules.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredRules.map((r) => r.id)));
    }
  };

  // Handle test notification
  const handleTestNotification = async (type: 'email' | 'sms' | 'push' | 'whatsapp') => {
    setTestingNotification(type);
    setTestResult(null);

    try {
      const response = await alertsApi.testNotification({
        type,
        alertType: formData.type || 'OVERSPEED',
      });

      setTestResult({
        type,
        success: true,
        message: response.data.message,
        preview: response.data.preview,
      });
    } catch (error: any) {
      const errorData = error.response?.data;
      setTestResult({
        type,
        success: false,
        message: errorData?.error || 'Erreur lors du test',
        preview: errorData?.preview,
      });
    } finally {
      setTestingNotification(null);
    }
  };

  // Fetch rules
  const { data: rulesData, isLoading } = useQuery({
    queryKey: ['alert-rules', typeFilter, activeFilter, tenantKey],
    queryFn: async () => {
      const params: Record<string, unknown> = {};
      if (typeFilter) params.type = typeFilter;
      if (activeFilter !== null) params.isActive = activeFilter;
      const response = await alertsApi.listRules(params as any);
      return response.data;
    },
  });

  // Fetch devices for selection
  const { data: devicesData } = useQuery({
    queryKey: ['devices-list', tenantKey],
    queryFn: async () => {
      const response = await devicesApi.list({ limit: 1000 });
      return response.data;
    },
  });

  // Fetch geofences for selection
  const { data: geofencesData } = useQuery({
    queryKey: ['geofences-list', tenantKey],
    queryFn: async () => {
      const response = await geofencesApi.list({ limit: 1000 });
      return response.data;
    },
  });

  // Fetch beacons for beacon alert selection
  const { data: beaconsData } = useQuery({
    queryKey: ['beacons-list', tenantKey],
    queryFn: async () => {
      const response = await beaconApi.list({ limit: 200 });
      return response.data;
    },
  });

  // Fetch fuel cards (only if FUEL module is active) for FUEL_THRESHOLD scope.
  const hasFuelModule = useHasModule('FUEL');
  const { data: fuelCardsResp } = useQuery({
    queryKey: ['fuel-cards-for-alert-rules', tenantKey],
    queryFn: async () => (await fuelApi.listCards()).data,
    enabled: hasFuelModule,
  });
  const fuelCards: FuelCard[] = fuelCardsResp?.data ?? [];

  // Fetch users for recipient selection
  const { data: usersData } = useQuery({
    queryKey: ['users-list', tenantKey],
    queryFn: async () => {
      const r = await usersApi.list({ limit: 500 });
      return r.data;
    },
  });

  const rules: AlertRule[] = rulesData?.data || [];
  const devices: Device[] = devicesData?.data || [];
  const geofences: Geofence[] = geofencesData?.data || [];
  const beacons: Array<{ id: string; name: string | null; macAddress: string }> = beaconsData?.data || [];
  const users: Array<{ id: string; firstName: string; lastName: string; email: string; role: string }> = usersData?.data || [];

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => alertsApi.createRule(data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
      closeModal();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: typeof formData }) =>
      alertsApi.updateRule(id, data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
      closeModal();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => alertsApi.deleteRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      alertsApi.updateRule(id, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
    },
  });

  // Bulk mutations
  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => alertsApi.bulkDeleteRules(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
      setSelectedIds(new Set());
    },
  });

  const bulkToggleMutation = useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      alertsApi.bulkToggleRules(ids, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
      setSelectedIds(new Set());
    },
  });

  const bulkRecipientsMutation = useMutation({
    mutationFn: ({ ids, recipientUserIds }: { ids: string[]; recipientUserIds: string[] }) =>
      alertsApi.bulkUpdateRecipients(ids, recipientUserIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
      setSelectedIds(new Set());
      setShowBulkRecipientsModal(false);
    },
  });

  // Filter rules by search
  const filteredRules = rules.filter((rule) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      rule.name.toLowerCase().includes(query) ||
      rule.description?.toLowerCase().includes(query) ||
      t(`alert_rules.type_${rule.type}_label`).toLowerCase().includes(query)
    );
  });

  const openModal = (rule?: AlertRule) => {
    if (rule) {
      setEditingRule(rule);
      // Filter deviceIds to only include devices that exist in current device list
      const validDeviceIds = new Set(devices.map((d) => d.id));
      setFormData({
        ...rule,
        deviceIds: rule.deviceIds.filter((id) => validDeviceIds.has(id)),
        geofences: undefined, // Remove the geofences relation for form
      });
    } else {
      setEditingRule(null);
      setFormData({ ...defaultRule });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingRule(null);
    setFormData({ ...defaultRule });
  };

  const handleSubmit = () => {
    // Clean up form data before sending — strip DB-only / relation fields
    const {
      id: _id,
      tenantId: _tenantId,
      createdAt: _createdAt,
      updatedAt: _updatedAt,
      geofences: _geofences,
      _count,
      ...cleanData
    } = formData as any;

    // Remove empty/null description
    if (!cleanData.description) {
      delete cleanData.description;
    }

    // Remove null webhookUrl
    if (cleanData.webhookUrl === null || cleanData.webhookUrl === undefined) {
      delete cleanData.webhookUrl;
    }

    // Sanitize conditions: replace NaN/null numeric values with defaults
    if (cleanData.conditions && typeof cleanData.conditions === 'object') {
      const sanitized: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(cleanData.conditions)) {
        if (value === null || value === undefined || (typeof value === 'number' && isNaN(value))) {
          continue; // Drop NaN/null values — backend treats missing as optional
        }
        sanitized[key] = value;
      }
      cleanData.conditions = sanitized;
    }

    // Sanitize cooldownMinutes
    if (typeof cleanData.cooldownMinutes === 'number' && isNaN(cleanData.cooldownMinutes)) {
      cleanData.cooldownMinutes = 5;
    }

    if (editingRule) {
      updateMutation.mutate({ id: editingRule.id, data: cleanData });
    } else {
      createMutation.mutate(cleanData);
    }
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleTypeChange = (type: AlertType) => {
    let newConditions: Record<string, unknown> = {};

    // Set default conditions based on type
    switch (type) {
      case 'OVERSPEED':
        newConditions = { speedLimit: 120 };
        break;
      case 'UNDERSPEED':
        newConditions = { minSpeed: 10 };
        break;
      case 'DEVICE_OFFLINE':
        newConditions = { offlineMinutes: 30 };
        break;
      case 'BATTERY_LOW':
      case 'FUEL_LOW':
        newConditions = { threshold: 20 };
        break;
      case 'IDLE_PROLONGED':
        newConditions = { durationMinutes: 15 };
        break;
      case 'POWER_CUT':
      case 'POWER_RESTORED':
      case 'LOW_POWER':
        newConditions = { threshold: 10 };
        break;
      case 'DEVICE_MOVING':
        newConditions = { speedThreshold: 5 };
        break;
      case 'DEVICE_STOPPED':
        newConditions = { speedThreshold: 3, minStopDuration: 5 };
        break;
      case 'HARD_ACCELERATION':
      case 'HARD_BRAKING':
        newConditions = { threshold: 0.4 };
        break;
      case 'HARD_CORNERING':
        newConditions = { threshold: 0.3, minSpeed: 20 };
        break;
      case 'FUEL_DROP':
      case 'FUEL_INCREASE':
        newConditions = { threshold: 10 };
        break;
      case 'BEACON_NEAR':
      case 'BEACON_AWAY':
      case 'BEACON_LOST':
        newConditions = { beaconId: '' };
        break;
      default:
        newConditions = {};
    }

    setFormData((prev) => ({
      ...prev,
      type,
      conditions: newConditions,
    }));
  };

  const handleDayToggle = (day: number) => {
    const currentDays = formData.scheduleDays || [];
    const newDays = currentDays.includes(day)
      ? currentDays.filter((d) => d !== day)
      : [...currentDays, day].sort();
    setFormData((prev) => ({ ...prev, scheduleDays: newDays }));
  };

  const handleDeviceToggle = (deviceId: string) => {
    const currentDevices = formData.deviceIds || [];
    const newDevices = currentDevices.includes(deviceId)
      ? currentDevices.filter((d) => d !== deviceId)
      : [...currentDevices, deviceId];
    setFormData((prev) => ({ ...prev, deviceIds: newDevices }));
  };

  const handleUserToggle = (userId: string) => {
    const current = formData.recipientUserIds || [];
    const updated = current.includes(userId)
      ? current.filter((id) => id !== userId)
      : [...current, userId];
    setFormData((prev) => ({ ...prev, recipientUserIds: updated }));
  };

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('alert_rules.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {t('alert_rules.subtitle')}
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => openModal()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Plus className="h-5 w-5" />
            {t('alert_rules.new_rule')}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-gray-200">
        <Link
          to="/alerts"
          className={clsx(
            'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
            location.pathname === '/alerts'
              ? 'border-green-600 text-green-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          )}
        >
          <List className="h-4 w-4" />
          {t('alerts.tab_history')}
        </Link>
        <Link
          to="/alerts/rules"
          className={clsx(
            'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
            location.pathname === '/alerts/rules'
              ? 'border-green-600 text-green-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          )}
        >
          <Settings className="h-4 w-4" />
          {t('alerts.tab_rules')}
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder={t('alert_rules.search_placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>

        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-gray-400" />

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as AlertType | '')}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500"
          >
            <option value="">{t('alert_rules.filter_all_types')}</option>
            {Object.entries(alertTypeConfig).map(([type]) => (
              <option key={type} value={type}>
                {t(`alert_rules.type_${type}_label`)}
              </option>
            ))}
          </select>

          <select
            value={activeFilter === null ? '' : activeFilter ? 'true' : 'false'}
            onChange={(e) =>
              setActiveFilter(e.target.value === '' ? null : e.target.value === 'true')
            }
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500"
          >
            <option value="">{t('alert_rules.filter_all_statuses')}</option>
            <option value="true">{t('alert_rules.status_active')}</option>
            <option value="false">{t('alert_rules.status_inactive')}</option>
          </select>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.size > 0 && canEdit && (
        <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-xl flex-wrap">
          <span className="text-sm font-medium text-blue-800 px-2 py-1 bg-blue-100 rounded-full">
            {t('alert_rules.selected_count', { count: selectedIds.size })}
          </span>
          <button
            onClick={() => bulkToggleMutation.mutate({ ids: Array.from(selectedIds), isActive: true })}
            disabled={bulkToggleMutation.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-lg transition-colors disabled:opacity-50"
          >
            <Power className="h-4 w-4" />
            Activer
          </button>
          <button
            onClick={() => bulkToggleMutation.mutate({ ids: Array.from(selectedIds), isActive: false })}
            disabled={bulkToggleMutation.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-orange-700 bg-orange-100 hover:bg-orange-200 rounded-lg transition-colors disabled:opacity-50"
          >
            <Power className="h-4 w-4" />
            {t('alert_rules.btn_disable')}
          </button>
          <button
            onClick={() => {
              setBulkRecipientUserIds([]);
              setShowBulkRecipientsModal(true);
            }}
            disabled={bulkRecipientsMutation.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-purple-700 bg-purple-100 hover:bg-purple-200 rounded-lg transition-colors disabled:opacity-50"
          >
            <Users className="h-4 w-4" />
            {t('alerts.recipients_title') || 'Destinataires'}
          </button>
          {canDelete && (
            <button
              onClick={() => {
                if (confirm(t('alert_rules.delete_bulk_confirm', { count: selectedIds.size }))) {
                  bulkDeleteMutation.mutate(Array.from(selectedIds));
                }
              }}
              disabled={bulkDeleteMutation.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-lg transition-colors disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" />
              {t('alert_rules.delete')}
            </button>
          )}
          <button
            onClick={() => setSelectedIds(new Set())}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors ml-auto"
          >
            <X className="h-4 w-4" />
            {t('alert_rules.btn_deselect')}
          </button>
        </div>
      )}

      {/* Rules List */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center text-gray-500">{t('common.loading')}</div>
        ) : filteredRules.length === 0 ? (
          <div className="p-12 text-center">
            <Bell className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">{t('alert_rules.empty_no_rules')}</p>
            {canEdit && (
              <button
                onClick={() => openModal()}
                className="mt-4 text-green-600 hover:text-green-700"
              >
                {t('alert_rules.btn_create_new')}
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {/* Select all header */}
            {canEdit && (
              <div className="px-4 py-2 bg-gray-50 flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={selectedIds.size === filteredRules.length && filteredRules.length > 0}
                  onChange={toggleSelectAll}
                  className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                <span className="text-xs text-gray-500">{t('alert_rules.btn_select_all')}</span>
              </div>
            )}
            {filteredRules.map((rule) => {
              const typeConfig = alertTypeConfig[rule.type];
              const TypeIcon = typeConfig.icon;
              const severity = severityOptions.find((s) => s.value === rule.severity);

              return (
                <div
                  key={rule.id}
                  className={clsx(
                    'p-4 hover:bg-gray-50 transition-colors',
                    !rule.isActive && 'opacity-60',
                    selectedIds.has(rule.id) && 'bg-blue-50/50'
                  )}
                >
                  <div className="flex items-start gap-4">
                    {/* Checkbox */}
                    {canEdit && (
                      <input
                        type="checkbox"
                        checked={selectedIds.has(rule.id)}
                        onChange={() => toggleSelect(rule.id)}
                        className="mt-3 rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                    )}

                    {/* Icon */}
                    <div
                      className={clsx(
                        'p-3 rounded-xl',
                        rule.isActive ? 'bg-green-100' : 'bg-gray-100'
                      )}
                    >
                      <TypeIcon
                        className={clsx(
                          'h-6 w-6',
                          rule.isActive ? 'text-green-600' : 'text-gray-400'
                        )}
                      />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900">{rule.name}</h3>
                        <span
                          className={clsx(
                            'px-2 py-0.5 rounded-full text-xs font-medium',
                            severity?.color
                          )}
                        >
                          {severity ? t(`alert_rules.severity_${severity.value}`) : ''}
                        </span>
                        {!rule.isActive && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            {t('alert_rules.status_inactive')}
                          </span>
                        )}
                      </div>

                      <p className="text-sm text-gray-500 mt-1">
                        {t(`alert_rules.type_${rule.type}_label`)}
                        {rule.description && ` - ${rule.description}`}
                      </p>

                      {/* Meta info */}
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                        {rule.notifyEmail && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" /> Email
                          </span>
                        )}
                        {rule.notifyPush && (
                          <span className="flex items-center gap-1">
                            <Smartphone className="h-3 w-3" /> Push
                          </span>
                        )}
                        {rule.notifySms && (
                          <span className="flex items-center gap-1">
                            <Smartphone className="h-3 w-3" /> SMS
                          </span>
                        )}
                        {rule.notifyWhatsapp && (
                          <span className="flex items-center gap-1">
                            <MessageCircle className="h-3 w-3" /> WhatsApp
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {rule.cooldownMinutes}min cooldown
                        </span>
                        {rule.deviceIds.length > 0 && (
                          <span className="flex items-center gap-1">
                            <Truck className="h-3 w-3" /> {t('alert_rules.device_count', { count: rule.deviceIds.length })}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {canEdit && (
                        <button
                          onClick={() => toggleMutation.mutate({ id: rule.id, isActive: !rule.isActive })}
                          className={clsx(
                            'p-2 rounded-lg transition-colors',
                            rule.isActive
                              ? 'text-green-600 hover:bg-green-50'
                              : 'text-gray-400 hover:bg-gray-100'
                          )}
                          title={rule.isActive ? t('alert_rules.toggle_deactivate') : t('alert_rules.toggle_activate')}
                        >
                          {rule.isActive ? (
                            <ToggleRight className="h-5 w-5" />
                          ) : (
                            <ToggleLeft className="h-5 w-5" />
                          )}
                        </button>
                      )}
                      {canEdit && (
                        <button
                          onClick={() => openModal(rule)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title={t('alert_rules.edit')}
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => {
                            if (confirm(t('alert_rules.delete_one_confirm'))) {
                              deleteMutation.mutate(rule.id);
                            }
                          }}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title={t('alert_rules.delete')}
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={closeModal} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  {editingRule ? t('alert_rules.modal_edit_title') : t('alert_rules.modal_create_title')}
                </h2>
                <button
                  onClick={closeModal}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('alert_rules.field_name_label')}
                    </label>
                    <input
                      type="text"
                      value={formData.name || ''}
                      onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder={t('alert_rules.field_name_placeholder')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('alert_rules.field_description_label')}
                    </label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                      placeholder={t('alert_rules.field_description_placeholder')}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('alert_rules.field_type_label')}
                      </label>
                      <select
                        value={formData.type || 'OVERSPEED'}
                        onChange={(e) => handleTypeChange(e.target.value as AlertType)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      >
                        {Object.entries(alertTypeConfig)
                          .filter(([type]) => type !== 'FUEL_THRESHOLD' || hasFuelModule)
                          .map(([type]) => (
                            <option key={type} value={type}>
                              {t(`alert_rules.type_${type}_label`)}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('alert_rules.field_severity_label')}
                      </label>
                      <select
                        value={formData.severity || 'MEDIUM'}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, severity: e.target.value as AlertSeverity }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      >
                        {severityOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {t(`alert_rules.severity_${option.value}`)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Type description */}
                  {formData.type && (
                    <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
                      <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-blue-700">
                        {t(`alert_rules.type_${formData.type}_desc`)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Conditions Section */}
                {formData.type && alertTypeConfig[formData.type].hasConditions && (
                  <div className="border rounded-lg">
                    <button
                      onClick={() => toggleSection('conditions')}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
                    >
                      <span className="font-medium text-gray-900">{t('alert_rules.section_conditions')}</span>
                      {expandedSections.conditions ? (
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                    {expandedSections.conditions && (
                      <div className="px-4 pb-4 space-y-4">
                        {formData.type === 'OVERSPEED' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('alert_rules.field_speed_limit')}
                            </label>
                            <input
                              type="number"
                              value={(formData.conditions as any)?.speedLimit || 120}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  conditions: { ...prev.conditions, speedLimit: parseInt(e.target.value) || 120 },
                                }))
                              }
                              min={0}
                              max={300}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            />
                          </div>
                        )}
                        {formData.type === 'UNDERSPEED' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('alert_rules.field_min_speed')}
                            </label>
                            <input
                              type="number"
                              value={(formData.conditions as any)?.minSpeed || 10}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  conditions: { ...prev.conditions, minSpeed: parseInt(e.target.value) || 10 },
                                }))
                              }
                              min={0}
                              max={100}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            />
                          </div>
                        )}
                        {formData.type === 'DEVICE_OFFLINE' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Delai avant alerte (minutes)
                            </label>
                            <input
                              type="number"
                              value={(formData.conditions as any)?.offlineMinutes || 30}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  conditions: { ...prev.conditions, offlineMinutes: parseInt(e.target.value) || 30 },
                                }))
                              }
                              min={5}
                              max={1440}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            />
                          </div>
                        )}
                        {(formData.type === 'BATTERY_LOW' || formData.type === 'FUEL_LOW') && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Seuil (%)
                            </label>
                            <input
                              type="number"
                              value={(formData.conditions as any)?.threshold || 20}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  conditions: { ...prev.conditions, threshold: parseInt(e.target.value) || 20 },
                                }))
                              }
                              min={1}
                              max={100}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            />
                          </div>
                        )}
                        {formData.type === 'IDLE_PROLONGED' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('alert_rules.field_idle_duration')}
                            </label>
                            <input
                              type="number"
                              value={(formData.conditions as any)?.durationMinutes || 15}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  conditions: { ...prev.conditions, durationMinutes: parseInt(e.target.value) || 15 },
                                }))
                              }
                              min={1}
                              max={120}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            />
                          </div>
                        )}
                        {formData.type === 'FUEL_THRESHOLD' && (
                          <div className="space-y-3 col-span-full">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Métrique</label>
                                <select
                                  value={((formData.conditions as Record<string, unknown>)?.metric as string) ?? 'amount'}
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      conditions: { ...prev.conditions, metric: e.target.value },
                                    }))
                                  }
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                                >
                                  <option value="amount">Montant (€)</option>
                                  <option value="volume">Litres</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Période</label>
                                <select
                                  value={((formData.conditions as Record<string, unknown>)?.period as string) ?? '30d'}
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      conditions: { ...prev.conditions, period: e.target.value },
                                    }))
                                  }
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                                >
                                  <option value="transaction">Cette transaction (par achat)</option>
                                  <option value="7d">7 derniers jours</option>
                                  <option value="30d">30 derniers jours</option>
                                  <option value="90d">90 derniers jours</option>
                                  <option value="current_month">Mois en cours</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Seuil (
                                  {((formData.conditions as Record<string, unknown>)?.metric as string) === 'volume' ? 'L' : '€'}
                                  )
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min={1}
                                  value={((formData.conditions as Record<string, unknown>)?.threshold as number) ?? ''}
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      conditions: { ...prev.conditions, threshold: parseFloat(e.target.value) || 0 },
                                    }))
                                  }
                                  placeholder="ex: 500"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Périmètre d'agrégation</label>
                              <div className="flex flex-wrap gap-3">
                                {(['device', 'card'] as const).map((s) => (
                                  <label key={s} className="inline-flex items-center gap-1.5 text-sm">
                                    <input
                                      type="radio"
                                      name="fuel-threshold-scope"
                                      value={s}
                                      checked={(((formData.conditions as Record<string, unknown>)?.scope as string) ?? 'device') === s}
                                      onChange={() =>
                                        setFormData((prev) => ({
                                          ...prev,
                                          conditions: { ...prev.conditions, scope: s },
                                        }))
                                      }
                                    />
                                    {s === 'device' ? 'Par véhicule' : 'Par carte essence'}
                                  </label>
                                ))}
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                "Par véhicule" : le total est calculé pour chaque véhicule. "Par carte" : pour chaque
                                numéro de carte essence. Le filtre véhicule (en bas) reste appliqué dans tous les cas.
                              </p>
                            </div>
                            {(((formData.conditions as Record<string, unknown>)?.scope as string) === 'card') && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Cartes essence concernées (laisser vide = toutes)
                                </label>
                                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50 space-y-1">
                                  {fuelCards.length === 0 ? (
                                    <p className="text-sm text-gray-500 italic">Aucune carte renseignée.</p>
                                  ) : (
                                    fuelCards.map((c) => {
                                      const selected = Array.isArray(
                                        (formData.conditions as Record<string, unknown>)?.fuelCardIds,
                                      )
                                        ? ((formData.conditions as Record<string, unknown>).fuelCardIds as string[])
                                        : [];
                                      const checked = selected.includes(c.id);
                                      return (
                                        <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer">
                                          <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={(e) => {
                                              const next = e.target.checked
                                                ? [...selected, c.id]
                                                : selected.filter((x) => x !== c.id);
                                              setFormData((prev) => ({
                                                ...prev,
                                                conditions: { ...prev.conditions, fuelCardIds: next },
                                              }));
                                            }}
                                          />
                                          <span className="font-mono text-xs">{c.cardNumber}</span>
                                          {c.label && <span className="text-gray-500">— {c.label}</span>}
                                        </label>
                                      );
                                    })
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {(formData.type === 'POWER_CUT' || formData.type === 'POWER_RESTORED' || formData.type === 'LOW_POWER') && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {formData.type === 'LOW_POWER' ? t('alert_rules.field_low_voltage_threshold') : t('alert_rules.field_voltage_threshold')}
                            </label>
                            <input
                              type="number"
                              value={(formData.conditions as any)?.threshold || 10}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  conditions: { ...prev.conditions, threshold: parseFloat(e.target.value) || 10 },
                                }))
                              }
                              min={0}
                              max={50}
                              step={0.5}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              {formData.type === 'POWER_CUT'
                                ? t('alert_rules.voltage_help_power_cut')
                                : formData.type === 'POWER_RESTORED'
                                  ? t('alert_rules.voltage_help_power_restored')
                                  : t('alert_rules.voltage_help_low')}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Geofences Selection (for geofence types) */}
                {(formData.type === 'GEOFENCE_ENTER' || formData.type === 'GEOFENCE_EXIT') && (
                  <div className="border rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('alert_rules.field_geofences_label')}
                    </label>
                    <div className="max-h-40 overflow-y-auto space-y-2">
                      {geofences.length === 0 ? (
                        <p className="text-sm text-gray-500">{t('alert_rules.no_geofence_available')}</p>
                      ) : (
                        geofences.map((geofence) => (
                          <label key={geofence.id} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(formData as any).geofenceIds?.includes(geofence.id) || false}
                              onChange={(e) => {
                                const currentIds = (formData as any).geofenceIds || [];
                                const newIds = e.target.checked
                                  ? [...currentIds, geofence.id]
                                  : currentIds.filter((id: string) => id !== geofence.id);
                                setFormData((prev) => ({ ...prev, geofenceIds: newIds } as any));
                              }}
                              className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                            />
                            <span className="text-sm text-gray-700">{geofence.name}</span>
                            <span className="text-xs text-gray-400">({geofence.type})</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Beacon Selection (for beacon alert types) */}
                {(formData.type === 'BEACON_NEAR' || formData.type === 'BEACON_AWAY' || formData.type === 'BEACON_LOST') && (
                  <div className="border rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('alert_rules.field_beacon_label')}
                    </label>
                    <select
                      value={(formData.conditions as any)?.beaconId || ''}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          conditions: { ...prev.conditions, beaconId: e.target.value || undefined },
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    >
                      <option value="">{t('alert_rules.all_beacons')}</option>
                      {beacons.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name || b.macAddress}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('alert_rules.beacon_help')}
                    </p>
                  </div>
                )}

                {/* Notifications Section */}
                <div className="border rounded-lg">
                  <button
                    onClick={() => toggleSection('notifications')}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
                  >
                    <span className="font-medium text-gray-900">{t('alert_rules.section_notifications')}</span>
                    {expandedSections.notifications ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                  {expandedSections.notifications && (
                    <div className="px-4 pb-4 space-y-4">
                      <div className="space-y-3">
                        {/* Email */}
                        <NotificationOption
                          icon={Mail}
                          label="Email"
                          description={t('alert_rules.channel_email_desc')}
                          checked={formData.notifyEmail || false}
                          onChange={(checked) => setFormData((prev) => ({ ...prev, notifyEmail: checked }))}
                          onTest={() => handleTestNotification('email')}
                          isLoading={testingNotification === 'email'}
                        />

                        {/* Push */}
                        <NotificationOption
                          icon={Bell}
                          label={t('alert_rules.channel_push_label')}
                          description={t('alert_rules.channel_push_desc')}
                          checked={formData.notifyPush || false}
                          onChange={(checked) => setFormData((prev) => ({ ...prev, notifyPush: checked }))}
                          onTest={() => handleTestNotification('push')}
                          isLoading={testingNotification === 'push'}
                        />

                        {/* SMS */}
                        <NotificationOption
                          icon={Smartphone}
                          label="SMS"
                          description={t('alert_rules.channel_sms_desc')}
                          checked={formData.notifySms || false}
                          onChange={(checked) => setFormData((prev) => ({ ...prev, notifySms: checked }))}
                          onTest={() => handleTestNotification('sms')}
                          isLoading={testingNotification === 'sms'}
                        />

                        {/* WhatsApp */}
                        <NotificationOption
                          icon={MessageCircle}
                          label="WhatsApp"
                          description={t('alert_rules.channel_whatsapp_desc')}
                          checked={formData.notifyWhatsapp || false}
                          onChange={(checked) => setFormData((prev) => ({ ...prev, notifyWhatsapp: checked }))}
                          onTest={() => handleTestNotification('whatsapp')}
                          isLoading={testingNotification === 'whatsapp'}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('alert_rules.field_cooldown_label')}
                        </label>
                        <input
                          type="number"
                          value={formData.cooldownMinutes || 5}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, cooldownMinutes: parseInt(e.target.value) || 5 }))
                          }
                          min={1}
                          max={1440}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {t('alert_rules.cooldown_help')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Recipients Section */}
                <div className="border rounded-lg">
                  <button
                    onClick={() => toggleSection('recipients')}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{t('alert_rules.section_recipients')}</span>
                      {(formData.recipientUserIds?.length || 0) > 0 && (
                        <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                          {t('alert_rules.selected_simple', { count: formData.recipientUserIds?.length })}
                        </span>
                      )}
                    </div>
                    {expandedSections.recipients ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                  {expandedSections.recipients && (
                    <div className="px-4 pb-4">
                      <p className="text-sm text-gray-500 mb-3">
                        {t('alert_rules.recipients_apply_all')}
                      </p>
                      <div className="max-h-48 overflow-y-auto space-y-2 border rounded-lg p-2">
                        {users.length === 0 ? (
                          <p className="text-sm text-gray-500 p-2">{t('alert_rules.no_users_available')}</p>
                        ) : (
                          users.map((user) => (
                            <label
                              key={user.id}
                              className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-gray-50"
                            >
                              <input
                                type="checkbox"
                                checked={formData.recipientUserIds?.includes(user.id) || false}
                                onChange={() => handleUserToggle(user.id)}
                                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                              />
                              <Users className="h-4 w-4 text-gray-400" />
                              <div className="flex-1 min-w-0">
                                <span className="text-sm text-gray-700">
                                  {user.firstName} {user.lastName}
                                </span>
                                <span className="text-xs text-gray-400 ml-2">{user.email}</span>
                              </div>
                              <span className="text-xs text-gray-400">{user.role}</span>
                            </label>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Schedule Section */}
                <div className="border rounded-lg">
                  <button
                    onClick={() => toggleSection('schedule')}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{t('alert_rules.section_schedule')}</span>
                      {formData.scheduleEnabled && (
                        <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
                          {t('alert_rules.schedule_active_badge')}
                        </span>
                      )}
                    </div>
                    {expandedSections.schedule ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                  {expandedSections.schedule && (
                    <div className="px-4 pb-4 space-y-4">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.scheduleEnabled || false}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, scheduleEnabled: e.target.checked }))
                          }
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-700">
                          {t('alert_rules.schedule_enable_label')}
                        </span>
                      </label>

                      {formData.scheduleEnabled && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('alert_rules.schedule_days_label')}
                            </label>
                            <div className="flex gap-2">
                              {DAY_KEYS.map((dayKey, index) => (
                                <button
                                  key={index}
                                  type="button"
                                  onClick={() => handleDayToggle(index === 0 ? 7 : index)}
                                  className={clsx(
                                    'w-10 h-10 rounded-lg text-sm font-medium transition-colors',
                                    formData.scheduleDays?.includes(index === 0 ? 7 : index)
                                      ? 'bg-green-600 text-white'
                                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                  )}
                                >
                                  {t(`alert_rules.${dayKey}`)}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('alert_rules.schedule_start_label')}
                              </label>
                              <input
                                type="time"
                                value={formData.scheduleStart || '08:00'}
                                onChange={(e) =>
                                  setFormData((prev) => ({ ...prev, scheduleStart: e.target.value }))
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('alert_rules.schedule_end_label')}
                              </label>
                              <input
                                type="time"
                                value={formData.scheduleEnd || '18:00'}
                                onChange={(e) =>
                                  setFormData((prev) => ({ ...prev, scheduleEnd: e.target.value }))
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Devices Section */}
                <div className="border rounded-lg">
                  <button
                    onClick={() => toggleSection('devices')}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{t('alert_rules.section_devices_inline')}</span>
                      {(formData.deviceIds?.length || 0) > 0 && (
                        <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                          {t('alert_rules.selected_simple', { count: formData.deviceIds?.length })}
                        </span>
                      )}
                    </div>
                    {expandedSections.devices ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                  {expandedSections.devices && (
                    <div className="px-4 pb-4">
                      <p className="text-sm text-gray-500 mb-3">
                        {t('alert_rules.devices_apply_all')}
                      </p>
                      <div className="max-h-48 overflow-y-auto space-y-2 border rounded-lg p-2">
                        {devices.length === 0 ? (
                          <p className="text-sm text-gray-500 p-2">{t('alert_rules.devices_none_available')}</p>
                        ) : (
                          devices.map((device) => (
                            <label
                              key={device.id}
                              className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-gray-50"
                            >
                              <input
                                type="checkbox"
                                checked={formData.deviceIds?.includes(device.id) || false}
                                onChange={() => handleDeviceToggle(device.id)}
                                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                              />
                              <Truck className="h-4 w-4 text-gray-400" />
                              <span className="text-sm text-gray-700">{device.name}</span>
                              {device.plate && (
                                <span className="text-xs text-gray-400">{device.plate}</span>
                              )}
                            </label>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex items-center justify-end gap-3">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!formData.name || createMutation.isPending || updateMutation.isPending}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Save className="h-5 w-5" />
                  {editingRule ? t('alert_rules.submit_save') : t('alert_rules.submit_create')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Recipients Modal */}
      {showBulkRecipientsModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowBulkRecipientsModal(false)} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="px-6 py-4 border-b flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('alert_rules.bulk_recipients_title', { count: selectedIds.size })}
                </h3>
                <button
                  onClick={() => setShowBulkRecipientsModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-6">
                <p className="text-sm text-gray-500 mb-3">
                  {t('alert_rules.bulk_recipients_help')}
                </p>
                <div className="max-h-64 overflow-y-auto space-y-2 border rounded-lg p-2">
                  {users.length === 0 ? (
                    <p className="text-sm text-gray-500 p-2">{t('alert_rules.no_users_available')}</p>
                  ) : (
                    users.map((u) => (
                      <label
                        key={u.id}
                        className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          checked={bulkRecipientUserIds.includes(u.id)}
                          onChange={() => {
                            setBulkRecipientUserIds((prev) =>
                              prev.includes(u.id)
                                ? prev.filter((id) => id !== u.id)
                                : [...prev, u.id]
                            );
                          }}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <Users className="h-4 w-4 text-gray-400" />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm text-gray-700">
                            {u.firstName} {u.lastName}
                          </span>
                          <span className="text-xs text-gray-400 ml-2">{u.email}</span>
                        </div>
                        <span className="text-xs text-gray-400">{u.role}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
              <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowBulkRecipientsModal(false)}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={() => {
                    bulkRecipientsMutation.mutate({
                      ids: Array.from(selectedIds),
                      recipientUserIds: bulkRecipientUserIds,
                    });
                  }}
                  disabled={bulkRecipientsMutation.isPending}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                >
                  <CheckSquare className="h-5 w-5" />
                  {bulkRecipientsMutation.isPending ? 'Application...' : 'Appliquer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test Result Modal */}
      {testResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg m-4 max-h-[80vh] overflow-hidden">
            <div className={clsx(
              'px-6 py-4 flex items-center gap-3',
              testResult.success ? 'bg-green-50' : 'bg-red-50'
            )}>
              {testResult.success ? (
                <CheckCircle className="h-6 w-6 text-green-600" />
              ) : (
                <XCircle className="h-6 w-6 text-red-600" />
              )}
              <div>
                <h3 className={clsx(
                  'font-semibold',
                  testResult.success ? 'text-green-900' : 'text-red-900'
                )}>
                  {testResult.success ? t('alert_rules.test_success') : t('alert_rules.test_failed')}
                </h3>
                <p className={clsx(
                  'text-sm',
                  testResult.success ? 'text-green-700' : 'text-red-700'
                )}>
                  {testResult.message}
                </p>
              </div>
            </div>

            {testResult.preview && (
              <div className="px-6 py-4 border-t max-h-96 overflow-auto">
                <h4 className="text-sm font-medium text-gray-700 mb-2">{t('alert_rules.preview_label')}</h4>
                {testResult.preview.title && (
                  <p className="text-sm font-semibold text-gray-900 mb-1">{testResult.preview.title}</p>
                )}
                {testResult.preview.body && (
                  <p className="text-sm text-gray-600 mb-3">{testResult.preview.body}</p>
                )}
                {testResult.type === 'whatsapp' && testResult.preview?.body && (
                  <p className="text-sm text-gray-600">{testResult.preview.body}</p>
                )}
              </div>
            )}

            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
              <button
                onClick={() => setTestResult(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Notification Option Component
function NotificationOption({
  icon: Icon,
  label,
  description,
  checked,
  onChange,
  onTest,
  isLoading,
  tooltip,
}: {
  icon: React.ElementType;
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  onTest: () => void;
  isLoading?: boolean;
  tooltip?: string;
}) {
  const { t } = useTranslation();
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className={clsx(
      'flex items-center justify-between p-3 border rounded-lg transition-colors',
      checked ? 'border-green-300 bg-green-50' : 'border-gray-200 hover:bg-gray-50'
    )}>
      <label className="flex items-center gap-3 cursor-pointer flex-1">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="rounded border-gray-300 text-green-600 focus:ring-green-500"
        />
        <Icon className={clsx('h-5 w-5', checked ? 'text-green-600' : 'text-gray-400')} />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className={clsx('text-sm font-medium', checked ? 'text-green-900' : 'text-gray-700')}>
              {label}
            </span>
            {tooltip && (
              <div className="relative">
                <button
                  type="button"
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <HelpCircle className="h-4 w-4" />
                </button>
                {showTooltip && (
                  <div className="absolute left-0 bottom-full mb-2 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-10">
                    {tooltip}
                  </div>
                )}
              </div>
            )}
          </div>
          <p className="text-xs text-gray-500">{description}</p>
        </div>
      </label>
      <button
        type="button"
        onClick={onTest}
        disabled={isLoading}
        className="ml-3 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center gap-1"
        title={t('alert_rules.test_notification')}
      >
        {isLoading ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <Send className="h-3 w-3" />
        )}
        {isLoading ? 'Envoi...' : 'Tester'}
      </button>
    </div>
  );
}
